import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import { Alert, Drawer } from "antd";
import gql from "fraql";
import get from "lodash/get";
import isBlank from "../../../../utils/isBlank";
import LayoutTabsState from "../../../LayoutTabsState";
import SpinPageContent from "../../../SpinPageContent";
import ViewTeamDrawerInvitedTab from "./ViewTeamDrawerInvitedTab";
import ViewTeamDrawerJoinedTab from "./ViewTeamDrawerJoinedTab";
import ViewTeamDrawerPendingTab from "./ViewTeamDrawerPendingTab";

const TAB_JOINED = "joined";
const TAB_PENDING = "pending";
const TAB_INVITED = "invited";

export const GET_TEAM = gql`
  query ViewTeamDrawer_GetTeam($teamId: String!) {
    Team_by_pk(id: $teamId) {
      id
      name
      Competition {
        id
        name
        SportMeet {
          id
          name
        }
      }
    }
  }
`;

const GET_REGISTRATION_INVITES_ACCESS = gql`
  query ViewTeamDrawer_GetRegistrationInvitesAccess($TeamId: ID!) {
    getRegistrationInvitesForTeam(TeamId: $TeamId) {
      affected_rows
    }
  }
`;

function ViewTeamDrawer({ visible, teamId, regId, handleClose, readOnly, initialTabKey }) {
  const [currentTab, setCurrentTab] = useState(TAB_JOINED);
  const normalizedTeamId = useMemo(() => {
    return typeof teamId === "string" ? teamId.trim() : teamId;
  }, [teamId]);
  const hasTeamId = !isBlank(normalizedTeamId);

  const { loading: teamLoading, error: teamError, data: teamData } = useQuery(GET_TEAM, {
    variables: { teamId: normalizedTeamId },
    skip: !hasTeamId,
  });

  const {
    loading: invitesAccessLoading,
    error: invitesAccessError,
    data: invitesAccessData,
  } = useQuery(GET_REGISTRATION_INVITES_ACCESS, {
    variables: { TeamId: normalizedTeamId },
    skip: !visible || !hasTeamId,
    fetchPolicy: "network-only",
  });

  const drawerTitle = useMemo(() => {
    if (teamError || !teamData) {
      return "Team";
    }

    const teamName = get(teamData, "Team_by_pk.name", "Team", "");
    const competitionName = get(teamData, "Team_by_pk.Competition.name", "Competition", "");
    const sportMeetName = get(teamData, "Team_by_pk.Competition.SportMeet.name", "");

    let name = `${competitionName} - ${teamName}`;

    if (sportMeetName) {
      name = `${sportMeetName} - ${name}`;
    }

    return name;
  }, [teamError, teamData]);

  const canManageInvites = useMemo(() => {
    if (invitesAccessLoading) {
      return false;
    }

    if (invitesAccessError) {
      return false;
    }

    return !!get(invitesAccessData, "getRegistrationInvitesForTeam");
  }, [
    invitesAccessLoading,
    invitesAccessError,
    invitesAccessData,
  ]);

  const tabItems = useMemo(() => {
    const items = [
      {
        key: TAB_JOINED,
        title: "Joined",
      },
      {
        key: TAB_PENDING,
        title: "Pending",
      },
    ];

    if (canManageInvites) {
      items.push({
        key: TAB_INVITED,
        title: "Invited",
      });
    }

    return items;
  }, [canManageInvites]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setCurrentTab(initialTabKey || TAB_JOINED);
  }, [visible, initialTabKey]);

  useEffect(() => {
    if (invitesAccessLoading) {
      return;
    }

    const isCurrentTabAvailable = tabItems.some(item => item.key === currentTab);

    if (!isCurrentTabAvailable) {
      setCurrentTab(TAB_JOINED);
    }
  }, [tabItems, currentTab, invitesAccessLoading]);

  const handleCloseDrawer = useCallback(() => {
    handleClose();

    // Change to the default "Joined" tab *after* the drawer has been closed, for better UX.
    setTimeout(() => {
      setCurrentTab(TAB_JOINED);
    }, 300);
  }, [handleClose]);

  const tabContent = useMemo(() => {
    if (teamLoading && !teamData) {
      return <SpinPageContent />;
    }

    if (teamError) {
      return (
        <Alert
          message="Team failed to load"
          description="Sorry, there was an issue loading the data for this page."
          type="error"
          showIcon
        />
      );
    }

    if (currentTab === TAB_JOINED) {
      return <ViewTeamDrawerJoinedTab teamId={normalizedTeamId} regId={regId} readOnly={readOnly} />;
    }

    if (currentTab === TAB_INVITED && canManageInvites) {
      return <ViewTeamDrawerInvitedTab teamId={normalizedTeamId} regId={regId} readOnly={readOnly} />;
    }

    return <ViewTeamDrawerPendingTab teamId={normalizedTeamId} regId={regId} readOnly={readOnly} />;
  }, [currentTab, teamLoading, teamError, teamData, regId, normalizedTeamId, readOnly, canManageInvites]);

  function handleClickTab({ key }) {
    setCurrentTab(key);
  }

  return (
    <Drawer
      title={
        <div>
          <div style={{ paddingLeft: 24, paddingBottom: 10 }}>{drawerTitle}</div>
          <LayoutTabsState items={tabItems} currentItemKey={currentTab} handleClick={handleClickTab} />
        </div>
      }
      onClose={handleCloseDrawer}
      visible={visible}
      width={
        // Wide enough for 4 columns in the table with each column having a suitable width.
        845
      }
      className="ant-drawer-tabs"
    >
      {tabContent}
    </Drawer>
  );
}

ViewTeamDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  teamId: PropTypes.string,
  regId: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  initialTabKey: PropTypes.oneOf([TAB_JOINED, TAB_PENDING, TAB_INVITED]),
};

ViewTeamDrawer.defaultProps = {
  teamId: undefined,
  regId: undefined,
  readOnly: false,
  initialTabKey: undefined,
};

export default ViewTeamDrawer;
